import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * A pill. Semantic colour here means a money state, never decoration:
 * green is paid/verified, amber is declared/under review, orange is revisions
 * requested, red is fixture data or destructive. The `success`/`warning`
 * variants used to be `bg-green-500` / `bg-yellow-500` — colours from outside
 * the palette that made a status read louder than the figure beside it.
 *
 * `stamp` is the badge that sits on a creator portrait or beside a name:
 * 9.5px/700 uppercase with wide tracking. It is one of only two places in the
 * product where uppercase is allowed, the other being the eyebrow label.
 */
export const badgeVariants = cva(
  "inline-flex items-center gap-1 whitespace-nowrap rounded-pill border leading-tight",
  {
    variants: {
      variant: {
        primary: "border-transparent bg-steel-600 text-white",
        default: "border-transparent bg-steel-600 text-white",
        accent: "bg-steel-50 text-steel-600 border-steel-100",
        secondary: "bg-steel-50 text-steel-600 border-steel-100",
        neutral: "border-transparent bg-neutral-100 text-neutral-600",
        outline: "bg-white text-neutral-600 border-neutral-200",
        success: "bg-success-bg text-success-fg border-success-border",
        warning: "bg-warning-bg text-warning-fg border-warning-border",
        changes: "bg-changes-bg text-changes-fg border-changes-border",
        danger: "bg-danger-bg text-danger-fg border-danger-border",
        destructive: "bg-danger-bg text-danger-fg border-danger-border",
        /* For badges sitting on top of a creator photograph. */
        onImage: "border-transparent bg-on-image-chip text-white",
      },
      stamp: {
        true: "px-2 py-1 text-label-sm uppercase",
        false: "px-[9px] py-[3px] text-chip",
      },
    },
    defaultVariants: {
      variant: "neutral",
      stamp: false,
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, stamp, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ variant, stamp, className }))}
      {...props}
    />
  ),
);
Badge.displayName = "Badge";

export { Badge };
